import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { RsvpSentStatsPoint } from '../../../../types/dashboard';
import { ADMIN_STAT_CHART_MAX_BAR_SIZE } from '../../reportChartConfig';

type RsvpsSentReportGraphProps = {
    points: RsvpSentStatsPoint[];
};

const RsvpsSentTooltip = ({
    active,
    payload,
    label,
}: TooltipProps<number, string>) => {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload as RsvpSentStatsPoint;

    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
            }}
        >
            <Typography variant='caption'>{label}</Typography>
            <Typography variant='body2'>Sent: {point.sentCount}</Typography>
            <Typography variant='body2'>
                Not sent: {point.notSentCount}
            </Typography>
        </Box>
    );
};

export default function RsvpsSentReportGraph({
    points,
}: RsvpsSentReportGraphProps) {
    const theme = useTheme();

    if (!points?.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No guest bookings found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%', height: 420, mt: 3 }}>
            <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                    data={points}
                    margin={{ left: 30 }}
                    maxBarSize={ADMIN_STAT_CHART_MAX_BAR_SIZE}
                >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='label' />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<RsvpsSentTooltip />} />
                    <Legend />
                    <Bar
                        dataKey='sentCount'
                        fill={theme.palette.textAccent.main}
                        name='Sent'
                    />
                    <Bar
                        dataKey='notSentCount'
                        fill={theme.palette.red.main}
                        name='Not sent'
                    />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
